/**
 * External store for live home button preview during slider/pad drags.
 * Keyed by feature ID to support multiple simultaneous home buttons.
 * Bypasses Redux so undo history is not polluted with intermediate values.
 *
 * Uses merge semantics: setPreviewHomeButton merges the incoming changes
 * with the existing preview for that ID, preventing X/Y overwrite issues
 * when OffsetPad fires onChangeX and onChangeY separately.
 */

export interface PreviewHomeButton {
  radius: number
  x: number
  y: number
}

let snapshot: Record<string, PreviewHomeButton> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Set (merge) preview for a specific feature ID.
 * @param id — feature ID
 * @param base — full fallback (from Redux) used when no preview record exists yet
 * @param changes — partial overrides to merge on top
 */
export const setPreviewHomeButton = (id: string, base: PreviewHomeButton, changes: Partial<PreviewHomeButton>) => {
  const existing = snapshot?.[id]
  snapshot = {
    ...snapshot,
    [id]: {
      radius: changes.radius ?? existing?.radius ?? base.radius,
      x: changes.x ?? existing?.x ?? base.x,
      y: changes.y ?? existing?.y ?? base.y,
    },
  }
  notify()
}

export const clearPreviewHomeButton = () => {
  snapshot = null
  notify()
}

export const getPreviewHomeButtonSnapshot = (): Record<string, PreviewHomeButton> | null => snapshot

export const subscribePreviewHomeButton = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
